// src/contextStore/AuthContext.js
import React, { createContext, useCallback, useEffect, useState } from "react";
import client from "../api/client";

export const AuthContext = createContext({
  user: null,
  token: null,
  initialized: false,
  setUser: () => {},
  logout: () => {},
});

/**
 * AuthProvider:
 * - Synchronously seeds user/token from localStorage so token is available immediately on first render.
 * - Then asynchronously validates token with /api/auth/me; only clear user token on 401 responses.
 * - IMPORTANT: this code purposely does NOT touch `admin_token` or `admin_user` keys.
 */
export function AuthProvider({ children }) {
  const normalizeToken = (t) => {
    if (!t) return null;
    const s = String(t).trim();
    if (!s || s === "null" || s === "undefined") return null;
    return s;
  };

  // Synchronously seed state from localStorage to avoid flash-of-unauthenticated on reload.
  const [token, setToken] = useState(() => {
    try {
      const raw = localStorage.getItem("token");
      return normalizeToken(raw);
    } catch (e) {
      console.warn("AuthContext: reading token from localStorage failed", e);
      return null;
    }
  });

  const [user, setUserState] = useState(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.warn("AuthContext: reading user from localStorage failed", e);
      return null;
    }
  });

  // initialized: set true once the async validation/hydration step completes
  const [initialized, setInitialized] = useState(false);

  // Keep axios header in sync immediately if we have a token (synchronous)
  useEffect(() => {
    try {
      // NOTE: we only set the *user* Authorization header here. Admin routes should prefer admin_token explicitly.
      if (token) {
        client.defaults = client.defaults || {};
        client.defaults.headers = client.defaults.headers || {};
        client.defaults.headers.common = client.defaults.headers.common || {};
        client.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } else {
        try {
          // remove user bearer header when no user token
          if (client.defaults && client.defaults.headers && client.defaults.headers.common) {
            delete client.defaults.headers.common["Authorization"];
          }
        } catch (e) {}
      }
    } catch (e) {
      console.warn("AuthContext: could not sync axios header with token", e);
    }
  }, [token]);

  // Async validation/hydration: validate token with server, update user if server returns user.
  useEffect(() => {
    let cancelled = false;

    async function validate() {
      try {
        // defensive clean of literal "null"/"undefined" strings in storage
        try {
          const rawPossible = localStorage.getItem("token");
          if (rawPossible === "null" || rawPossible === "undefined") {
            localStorage.removeItem("token");
          }
        } catch (e) {}

        const localToken = normalizeToken(localStorage.getItem("token"));
        const localUserRaw = localStorage.getItem("user");

        console.log("AuthContext.hydrate: found local user?", !!localUserRaw, "rawToken:", localToken ? "[REDACTED]" : null);

        if (!localToken) {
          // No token stored - nothing to validate. Mark initialized and exit.
          if (!cancelled) setInitialized(true);
          return;
        }

        try {
          // attach header explicitly as fallback
          const res = await client.get("/api/auth/me", {
            headers: { Authorization: `Bearer ${localToken}` },
          });

          console.log(
            "AuthContext.hydrate: /api/auth/me response",
            "status=",
            res?.status,
            "ok=",
            !!(res && res.data && res.data.ok),
            "userPresent=",
            !!(res && res.data && res.data.user)
          );

          if (cancelled) return;

          if (res?.data?.ok && res.data.user) {
            // keep context/user in sync with server
            setUserState(res.data.user);
            try {
              localStorage.setItem("user", JSON.stringify(res.data.user));
            } catch (e) {}
            // ensure token also persisted (some flows set token later)
            setToken(localToken);
          } else {
            // Unexpected response shape: treat as invalid session and clear user auth ONLY.
            console.warn("AuthContext.hydrate: auth/me did not return ok,user -> clearing user auth");
            setUserState(null);
            setToken(null);
            try {
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              // DO NOT remove admin keys: admin_token/admin_user should be left alone.
            } catch (e) {}
          }
        } catch (err) {
          if (cancelled) return;
          const status = err?.response?.status;
          console.warn("AuthContext.hydrate: /api/auth/me request failed, status=", status);

          if (status === 401) {
            // invalid user token -> clear user token/user, but preserve admin session keys.
            console.warn("AuthContext.hydrate: token invalid (401) — clearing user auth but preserving admin session");
            setUserState(null);
            setToken(null);
            try {
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              // IMPORTANT: do NOT call localStorage.removeItem('admin_token') here
            } catch (e) {}
          } else {
            // network/other error: preserve local auth so user isn't logged out by transient failures
            console.warn("AuthContext.hydrate: /api/auth/me failed with non-401, preserving local auth for now", err);
          }
        }
      } finally {
        if (!cancelled) setInitialized(true);
      }
    }

    validate();

    return () => {
      cancelled = true;
    };
  }, []);

  // persist user changes to localStorage
  useEffect(() => {
    try {
      if (user) localStorage.setItem("user", JSON.stringify(user));
      else localStorage.removeItem("user");
    } catch (e) {
      console.warn("AuthContext: cannot write user to localStorage", e);
    }
  }, [user]);

  // persist token changes to localStorage (and sync axios header is handled by separate effect)
  useEffect(() => {
    try {
      if (token) localStorage.setItem("token", token);
      else localStorage.removeItem("token");
    } catch (e) {
      console.warn("AuthContext: cannot write token to localStorage", e);
    }
  }, [token]);

  // setUser convenience function:
  // - Only update token if caller provides a second argument.
  // - This prevents accidental token clearing when callers only want to update the user object.
  const setUser = useCallback(
    (nextUser, nextToken) => {
      setUserState(nextUser || null);

      if (arguments.length > 1) {
        const normalized = normalizeToken(nextToken);
        setToken(normalized || null);
      }
      return { user: nextUser || null, token: arguments.length > 1 ? (nextToken || null) : token };
    },
    [token]
  );

  const logout = useCallback(() => {
  // Determine if this logout is from an admin session or user session
  const isAdminSession =
    localStorage.getItem("admin_token") ||
    localStorage.getItem("admin_user");

  // Clear user session only
  setUserState(null);
  setToken(null);
  try {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    // do NOT remove admin_token/admin_user here
  } catch (e) {}

  // Redirect based on session type
  if (isAdminSession) {
    window.location.href = "/admin/login";
  } else {
    window.location.href = "/login";
  }
}, []);


  return (
    <AuthContext.Provider value={{ user, token, initialized, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
